import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, forwardRef, input, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SvgIconComponent, SvgIcons, injectRegisterIcons } from '@ngneat/svg-icon';

import { getRuntimeUniqueId } from '@shared/utils/runtime-unique-id';

import { appClose16Icon } from '@icons/close-16';

@Component({
  selector: 'app-text-input',
  imports: [SvgIconComponent, NgClass],
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextInputComponent),
    },
  ],
  templateUrl: './text-input.component.html',
  styleUrl: './text-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextInputComponent implements ControlValueAccessor {
  public label = input<string>();
  public leftIcon = input<SvgIcons>();
  public leftIconSize = input<ReturnType<SvgIconComponent['size']>>('20');
  public placeholder = input<string>();
  public inputClasses = input<string>('');
  public size = input<'medium' | 'large'>('medium');
  public isValid = input<boolean | undefined>();
  public error = input<string>();
  public errorInFlow = input(false);
  public isRequired = input<boolean>(false);
  public type = input<'search' | 'text' | 'time'>('text');
  public clearable = input<boolean>(false);
  public readonly = input<boolean>(false);
  public inputId = input<string>();
  public inputRole = input<string>();
  public autocomplete = input<string>();
  public ariaLabel = input<string>();
  public ariaAutocomplete = input<string>();
  public ariaControls = input<string>();
  public ariaActiveDescendant = input<string | null>();
  public ariaExpanded = input<boolean | undefined>();

  public readonly uuid = `${getRuntimeUniqueId()}`;
  public readonly disabled = signal(false);
  public readonly inputElement = viewChild.required<ElementRef<HTMLInputElement>>('inputElement');

  public value = signal('');

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    injectRegisterIcons([appClose16Icon]);
  }

  public onInput(event: Event): void {
    if (this.readonly() || this.disabled()) {
      return;
    }
    const target = event.target as HTMLInputElement;
    this.value.set(target.value);
    this.onChange(target.value);
  }

  public clear(): void {
    if (this.readonly() || this.disabled()) {
      return;
    }
    this.value.set('');
    this.onChange('');
  }

  public onBlur(): void {
    this.onTouched();
  }

  public registerOnChange(onChange: (value: string) => void): void {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  public writeValue(value: string): void {
    this.value.set(value);
  }

  public openTimePicker(inputElement: HTMLInputElement): void {
    if (this.readonly() || this.disabled()) {
      return;
    }
    if ('showPicker' in inputElement) {
      inputElement.showPicker();
    } else {
      (inputElement as HTMLInputElement).focus();
    }
  }

  public focus(): void {
    this.inputElement().nativeElement.focus();
  }

  public isInputTarget(target: EventTarget | null): boolean {
    return target === this.inputElement().nativeElement;
  }
}
